// Query parameter types for type-safe query keys
import type { PatientListFilters } from "@/types/patient";

export type { PatientListFilters };


// Common pagination params
export interface PaginationParams {
  page?: number;
  per_page?: number;
}

// User list params
export interface UserListParams extends PaginationParams {
  search?: string;
  status?: "active" | "inactive";
  role_id?: number;
}


// Branch list params
export interface BranchListParams extends PaginationParams {
  search?: string;
  city_id?: number;
}

// Resource list params
export interface ResourceListParams extends PaginationParams {
  search?: string;
  branch_id?: number;
  resource_type_id?: number;
}

// Role list params
export interface RoleListParams extends PaginationParams {
  search?: string;
  is_branch_scoped?: boolean;
}

// Lead list params
export interface LeadListParams extends PaginationParams {
  search?: string;
  status_id?: number;
  assigned_to_id?: number;
  from_date?: string;
  to_date?: string;
  phone?: string;
}

// Lead search filters
export interface LeadSearchFilters {
  phone?: string;
  first_name?: string;
  last_name?: string;
  assigned_to?: string;
  from_date?: string;
  to_date?: string;
}

// Agent list params
export interface AgentListParams extends PaginationParams {
  search?: string;
  status?: string;
}

// Lead status list params
export interface LeadStatusListParams extends PaginationParams {
  search?: string;
}

// Appointment status list params
export interface AppointmentStatusListParams extends PaginationParams {
  search?: string;
}

// Centralized query keys for type safety
export const queryKeys = {
  // User queries
  users: {
    all: ["users"] as const,
    current: () => [...queryKeys.users.all, "current"] as const,
    roles: (id: number) => [...queryKeys.users.all, "roles", id] as const,
    branchRoles: (id: number) =>
      [...queryKeys.users.all, "branch-roles", id] as const,
    branches: (id: number) => [...queryKeys.users.all, "branches", id] as const,
    branchRolesForBranch: (userId: number, branchId: number) =>
      [...queryKeys.users.all, "branch-roles", userId, branchId] as const,
    list: (params?: UserListParams) =>
      [...queryKeys.users.all, "list", params] as const,
  },

  // Cities queries
  cities: {
    all: ["cities"] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.cities.all, "list", params] as const,
    detail: (id: number) => [...queryKeys.cities.all, "detail", id] as const,
    search: (query: string, locale: string, limit: number) =>
      [...queryKeys.cities.all, "search", query, locale, limit] as const,
  },

  // Resource types queries
  resourceTypes: {
    all: ["resourceTypes"] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.resourceTypes.all, "list", params] as const,
    detail: (id: number) =>
      [...queryKeys.resourceTypes.all, "detail", id] as const,
    search: (query: string, locale: string, limit: number) =>
      [...queryKeys.resourceTypes.all, "search", query, locale, limit] as const,
  },

  // Payment types queries
  paymentTypes: {
    all: ["paymentTypes"] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.paymentTypes.all, "list", params] as const,
    detail: (id: number) =>
      [...queryKeys.paymentTypes.all, "detail", id] as const,
    search: (query: string, locale: string, limit: number) =>
      [...queryKeys.paymentTypes.all, "search", query, locale, limit] as const,
  },

  // Cashless payment sources queries
  cashlessPaymentSources: {
    all: ["cashlessPaymentSources"] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.cashlessPaymentSources.all, "list", params] as const,
    detail: (id: number) =>
      [...queryKeys.cashlessPaymentSources.all, "detail", id] as const,
    search: (query: string, locale: string, limit: number) =>
      [...queryKeys.cashlessPaymentSources.all, "search", query, locale, limit] as const,
  },

  // Modality types queries
  modalityTypes: {
    all: ["modalityTypes"] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.modalityTypes.all, "list", params] as const,
    detail: (id: number) =>
      [...queryKeys.modalityTypes.all, "detail", id] as const,
    search: (query: string, locale: string, limit: number) =>
      [...queryKeys.modalityTypes.all, "search", query, locale, limit] as const,
  },

  // Positions queries
  positions: {
    all: ["positions"] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.positions.all, "list", params] as const,
    detail: (id: number) =>
      [...queryKeys.positions.all, "detail", id] as const,
    search: (query: string, locale: string, limit: number) =>
      [...queryKeys.positions.all, "search", query, locale, limit] as const,
  },

  // Radiology item statuses queries

  // Branches queries
  branches: {
    all: ["branches"] as const,
    list: (params?: BranchListParams) =>
      [...queryKeys.branches.all, "list", params] as const,
    detail: (id: number) => [...queryKeys.branches.all, "detail", id] as const,
    schedules: (branchId: number) =>
      [...queryKeys.branches.all, "schedules", branchId] as const,
    calendarData: (branchId: number, date: string) =>
      [...queryKeys.branches.all, "calendar-data", branchId, date] as const,
  },

  // Resources queries
  resources: {
    all: ["resources"] as const,
    list: (params?: ResourceListParams) =>
      [...queryKeys.resources.all, "list", params] as const,
    detail: (id: number) => [...queryKeys.resources.all, "detail", id] as const,
    schedules: (resourceId: number) =>
      [...queryKeys.resources.all, "schedules", resourceId] as const,
    breaks: (resourceId: number) =>
      [...queryKeys.resources.all, "breaks", resourceId] as const,
    exceptionDays: (resourceId: number) =>
      [...queryKeys.resources.all, "exceptionDays", resourceId] as const,
  },

  roles: {
    all: ["roles"] as const,
    list: (params?: RoleListParams) =>
      [...queryKeys.roles.all, "list", params] as const,
    listWithParams: (params?: RoleListParams) =>
      [...queryKeys.roles.all, "listWithParams", params] as const,
    detail: (id: number) => [...queryKeys.roles.all, "detail", id] as const,
    users: (id: number) => [...queryKeys.roles.all, "users", id] as const,
    capabilities: (id: number) =>
      [...queryKeys.roles.all, "capabilities", id] as const,
  },

  // Permission groups — the authoring layer over Casbin.
  capabilities: {
    all: ["capabilities"] as const,
    list: () => [...queryKeys.capabilities.all, "list"] as const,
    coverage: () => [...queryKeys.capabilities.all, "coverage"] as const,
    palette: () => [...queryKeys.capabilities.all, "palette"] as const,
  },

  // Leads queries
  leads: {
    all: ["leads"] as const,
    list: (filters?: LeadListParams) =>
      [...queryKeys.leads.all, "list", filters] as const,
    byStatus: (statusId: number) =>
      [...queryKeys.leads.all, "byStatus", statusId] as const,
    infiniteByStatus: (
      statusId: number,
      pageSize: number,
      searchFilters?: LeadSearchFilters
    ) =>
      [
        ...queryKeys.leads.all,
        "infiniteByStatus",
        statusId,
        pageSize,
        searchFilters,
      ] as const,
    detail: (id: number) => [...queryKeys.leads.all, "detail", id] as const,
    timeline: (id: number) => [...queryKeys.leads.all, "timeline", id] as const,
    comments: (id: number) => [...queryKeys.leads.all, "comments", id] as const,
    callHistory: (id: number) =>
      [...queryKeys.leads.all, "callHistory", id] as const,
    whatsappChats: (id: number) =>
      [...queryKeys.leads.all, "whatsappChats", id] as const,
    search: (filters: LeadSearchFilters) =>
      [...queryKeys.leads.all, "search", filters] as const,
    counts: (filters?: LeadSearchFilters) =>
      [...queryKeys.leads.all, "counts", filters] as const,
  },

  // Name by IIN queries
  nameByInn: {
    all: ["nameByInn"] as const,
    byIin: (iin: string) => [...queryKeys.nameByInn.all, "byIin", iin] as const,
  },

  // Agents queries
  agents: {
    all: ["agents"] as const,
    list: (params?: AgentListParams) =>
      [...queryKeys.agents.all, "list", params] as const,
    detail: (id: number) => [...queryKeys.agents.all, "detail", id] as const,
    statuses: (params?: AgentListParams) =>
      [...queryKeys.agents.all, "statuses", params] as const,
    currentCall: (id: number) =>
      [...queryKeys.agents.all, "currentCall", id] as const,
    byUser: (userId: number) =>
      [...queryKeys.agents.all, "byUser", userId] as const,
  },

  // Menu Items queries
  menuItems: {
    all: ["menuItems"] as const,
  },

  // Call-center analytics dashboard
  callCenter: {
    all: ["callCenter"] as const,
    stats: (period: string) =>
      [...queryKeys.callCenter.all, "stats", period] as const,
    calls: (params?: Record<string, unknown>) =>
      [...queryKeys.callCenter.all, "calls", params] as const,
    /** Every call list (drill-down and call log), for SSE invalidation. */
    callsAll: () => [...queryKeys.callCenter.all, "calls"] as const,
    callLogFilters: () => [...queryKeys.callCenter.all, "call-log-filters"] as const,
    pbxStatus: () => [...queryKeys.callCenter.all, "pbx-status"] as const,
  },

  // PBX settings: the call-centre page (/call-center/settings/pbx,
  // pbx.manage) and the administrator page (/settings/pbx, pbx.admin). Every
  // write bumps a section version, so mutations invalidate `all`, which keeps
  // both pages honest.
  pbx: {
    all: ["pbx"] as const,
    queues: () => [...queryKeys.pbx.all, "queues"] as const,
    queue: (id: number) => [...queryKeys.pbx.queues(), "detail", id] as const,
    nextFreeExtension: (domainId?: number) =>
      [...queryKeys.pbx.all, "next-free-extension", domainId] as const,
    extensions: (params?: Record<string, unknown>) =>
      [...queryKeys.pbx.all, "extensions", params] as const,
    queueAgents: () => [...queryKeys.pbx.all, "queue-agents"] as const,
    queueTiers: (params?: Record<string, unknown>) =>
      [...queryKeys.pbx.all, "queue-tiers", params] as const,
    gateways: () => [...queryKeys.pbx.all, "gateways"] as const,
    ivrMenus: () => [...queryKeys.pbx.all, "ivr-menus"] as const,
    flows: () => [...queryKeys.pbx.all, "flows"] as const,
    flow: (uuid: string) => [...queryKeys.pbx.all, "flows", "detail", uuid] as const,
    flowCalls: (uuid: string) => [...queryKeys.pbx.all, "flows", "calls", uuid] as const,
    schedules: () => [...queryKeys.pbx.all, "schedules"] as const,
    inboundRoutes: () => [...queryKeys.pbx.all, "inbound-routes"] as const,
    outboundRoutes: () => [...queryKeys.pbx.all, "outbound-routes"] as const,
    recording: () => [...queryKeys.pbx.all, "recording"] as const,
    // The voicemail inbox and the phone-settings page.
    voicemails: (params?: Record<string, unknown>) =>
      [...queryKeys.pbx.all, "voicemails", params] as const,
    voicemailBoxes: () => [...queryKeys.pbx.all, "voicemail-boxes"] as const,
    // Under "voicemails" so the inbox's invalidations refresh it too.
    voicemailForCall: (callRecordId: number) =>
      [...queryKeys.pbx.all, "voicemails", "call", callRecordId] as const,
    phoneSettings: () => [...queryKeys.pbx.all, "phone-settings"] as const,
    configVersions: () => [...queryKeys.pbx.all, "config-versions"] as const,
    dialplans: (params?: Record<string, unknown>) =>
      [...queryKeys.pbx.all, "dialplans", params] as const,
    sipProfiles: () => [...queryKeys.pbx.all, "sip-profiles"] as const,
    acls: () => [...queryKeys.pbx.all, "acls"] as const,
    vars: () => [...queryKeys.pbx.all, "vars"] as const,
    localStreams: () => [...queryKeys.pbx.all, "local-streams"] as const,
    agentProvisioning: (agentId: number) =>
      [...queryKeys.pbx.all, "agent-provisioning", agentId] as const,
  },

  // Management reports
  reports: {
    all: ["reports"] as const,
    reconciliation: (params?: Record<string, unknown>) =>
      [...queryKeys.reports.all, "reconciliation", params] as const,
  },

  // Lead Close Reasons queries
  leadCloseReasons: {
    all: ["leadCloseReasons"] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.leadCloseReasons.all, "list", params] as const,
    detail: (id: number) =>
      [...queryKeys.leadCloseReasons.all, "detail", id] as const,
    byCode: (code: string) =>
      [...queryKeys.leadCloseReasons.all, "byCode", code] as const,
    search: (query: string, locale: string, limit: number) =>
      [...queryKeys.leadCloseReasons.all, "search", query, locale, limit] as const,
  },

  // Lead Statuses queries
  leadStatuses: {
    all: ["leadStatuses"] as const,
    list: (params?: LeadStatusListParams) =>
      [...queryKeys.leadStatuses.all, "list", params] as const,
  },

  // Appointment Statuses queries
  appointmentStatuses: {
    all: ["appointmentStatuses"] as const,
    list: (params?: AppointmentStatusListParams) =>
      [...queryKeys.appointmentStatuses.all, "list", params] as const,
    detail: (id: number) =>
      [...queryKeys.appointmentStatuses.all, "detail", id] as const,
  },

  // Contacts queries
  contacts: {
    all: ["contacts"] as const,
    detail: (id: number) => [...queryKeys.contacts.all, "detail", id] as const,
    history: (id: number) =>
      [...queryKeys.contacts.all, "history", id] as const,
    callHistory: (id: number) =>
      [...queryKeys.contacts.all, "callHistory", id] as const,
  },

  // Patients queries
  patients: {
    all: ["patients"] as const,
    detail: (id: number) => [...queryKeys.patients.all, "detail", id] as const,
    byIIN: (iin: string) => [...queryKeys.patients.all, "byIIN", iin] as const,
    contacts: (id: number) =>
      [...queryKeys.patients.all, "contacts", id] as const,
    list: (filters: PatientListFilters) =>
      [...queryKeys.patients.all, "list", filters] as const,
    full: (id: number) => [...queryKeys.patients.all, "full", id] as const,
    communications: (id: number, kind: string | null) =>
      [...queryKeys.patients.all, "communications", id, kind] as const,
  },

  // Lookups queries
  lookups: {
    all: ["lookups"] as const,
    list: (location?: string) =>
      [...queryKeys.lookups.all, "list", location] as const,
  },

  // Lookup locations queries
  lookupLocations: {
    all: ["lookupLocations"] as const,
    list: () => [...queryKeys.lookupLocations.all, "list"] as const,
    detail: (id: number) =>
      [...queryKeys.lookupLocations.all, "detail", id] as const,
  },

  // Call Statuses queries
  callStatuses: {
    all: ["callStatuses"] as const,
  },

  // WhatsApp chats queries
  whatsappChats: {
    all: ["whatsappChats"] as const,
    withAnsweredCount: () =>
      [...queryKeys.whatsappChats.all, "withAnsweredCount"] as const,
  },

  // Instagram chats queries
  instagramChats: {
    all: ["instagramChats"] as const,
    withUnansweredCount: () =>
      [...queryKeys.instagramChats.all, "withUnansweredCount"] as const,
  },

  // Consumables catalog queries
  consumables: {
    all: ["consumables"] as const,
    list: (params?: Record<string, unknown>) =>
      [...queryKeys.consumables.all, "list", params] as const,
    detail: (id: number) =>
      [...queryKeys.consumables.all, "detail", id] as const,
    search: (query: string, locale: string, limit: number) =>
      [...queryKeys.consumables.all, "search", query, locale, limit] as const,
    settings: (id: number, branchId: number) =>
      [...queryKeys.consumables.all, "settings", id, branchId] as const,
    bom: (serviceOptionId: number) =>
      [...queryKeys.consumables.all, "bom", serviceOptionId] as const,
  },

  // Consumable prices queries
  consumablePrices: {
    all: ["consumablePrices"] as const,
    list: (consumableId: number, branchId: number) =>
      [...queryKeys.consumablePrices.all, "list", consumableId, branchId] as const,
  },

  // Inventory (stock / movements / alerts) queries
  inventory: {
    all: ["inventory"] as const,
    stock: (consumableId: number, branchId: number) =>
      [...queryKeys.inventory.all, "stock", consumableId, branchId] as const,
    movements: (consumableId: number, branchId: number) =>
      [...queryKeys.inventory.all, "movements", consumableId, branchId] as const,
    lowStock: (branchId: number) =>
      [...queryKeys.inventory.all, "lowStock", branchId] as const,
    expiring: (branchId: number, withinDays: number) =>
      [...queryKeys.inventory.all, "expiring", branchId, withinDays] as const,
  },

  // Call records queries
  callRecords: {
    all: ["callRecords"] as const,
    missed: (page: number, pageSize: number) =>
      [...queryKeys.callRecords.all, "missed", page, pageSize] as const,
    missedCount: () => [...queryKeys.callRecords.all, "missedCount"] as const,
  },
} as const;
